"use client";

import type { ReactNode, RefObject } from "react";
import {
  DiagramFrame,
  Label,
  SHAPE,
} from "@/components/tools/geometry/diagram";
import type { NetKind, SolidFieldId } from "@/lib/tool-kit/geometry/solids";

/*
 * Flat nets of the solids, drawn to the proportions typed. Each face carries the
 * expression for its area, so the surface area reads straight off the net: add the faces.
 * A legend line under the figure gives the measurements.
 */

export interface NetDiagramProps {
  net: Exclude<NetKind, "none">;
  title: string;
  svgRef: RefObject<SVGSVGElement | null>;
  /** The measurements as numbers in one unit. */
  values: Partial<Record<SolidFieldId, number>>;
  /** Slant heights as numbers in the same unit. */
  slant: { cone?: number; onLength?: number; onWidth?: number };
  /** "r = 3 cm", "l = 5 cm" and the like, shown under the net. */
  legend: string[];
}

interface Point {
  x: number;
  y: number;
}

interface DrawProps {
  value: (id: SolidFieldId) => number;
  slant: NetDiagramProps["slant"];
}

const AREA_W = 320;
const AREA_H = 172;
const LEFT = 20;
const TOP = 14;
const TWO_PI = 2 * Math.PI;
const FILL = SHAPE.fill;

const pt = (p: Point) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`;

function Piece({ d, shade = 1 }: { d: string; shade?: number }) {
  return (
    <path
      d={d}
      fill={FILL}
      fillOpacity={0.12 * shade}
      stroke={SHAPE.stroke}
      strokeLinejoin="round"
      strokeWidth={1.8}
    />
  );
}

function Tag({
  x,
  y,
  children,
}: {
  x: number;
  y: number;
  children: ReactNode;
}) {
  return (
    <Label color={SHAPE.ink} size={11} x={x} y={y + 4}>
      {children}
    </Label>
  );
}

const rectPath = (x: number, y: number, w: number, h: number) =>
  `M${x} ${y} h${w} v${h} h${-w} Z`;

const polygonPath = (points: Point[]) => `M${points.map(pt).join(" L")} Z`;

/** Thin figures stay legible: nothing is drawn thinner than a fifth of the largest. */
function floor(values: number[]): number[] {
  const top = Math.max(...values, Number.MIN_VALUE);
  return values.map((v) => Math.max(v, top * 0.2));
}

function layoutFor(
  width: number,
  height: number
): { k: number; x0: number; y0: number } {
  const k = Math.min(AREA_W / width, AREA_H / height);
  return {
    k,
    x0: LEFT + (AREA_W - width * k) / 2,
    y0: TOP + (AREA_H - height * k) / 2,
  };
}

function BoxNet({ value }: DrawProps) {
  const [l, w, h] = floor([
    value("length") || value("side"),
    value("width") || value("side"),
    value("height") || value("side"),
  ]);
  const cube = value("side") > 0;
  const { k, x0, y0 } = layoutFor(2 * l + 2 * w, 2 * w + h);
  const X = (u: number) => x0 + u * k;
  const Y = (u: number) => y0 + u * k;
  const faces = [
    { x: 0, y: w, fw: w, fh: h, tag: cube ? "a × a" : "w × h" },
    { x: w, y: w, fw: l, fh: h, tag: cube ? "a × a" : "l × h" },
    { x: w + l, y: w, fw: w, fh: h, tag: cube ? "a × a" : "w × h" },
    { x: 2 * w + l, y: w, fw: l, fh: h, tag: cube ? "a × a" : "l × h" },
    { x: w, y: 0, fw: l, fh: w, tag: cube ? "a × a" : "l × w" },
    { x: w, y: w + h, fw: l, fh: w, tag: cube ? "a × a" : "l × w" },
  ];
  return (
    <>
      {faces.map((f) => (
        <g key={`${f.x}-${f.y}`}>
          <Piece d={rectPath(X(f.x), Y(f.y), f.fw * k, f.fh * k)} />
          <Tag x={X(f.x + f.fw / 2)} y={Y(f.y + f.fh / 2)}>
            {f.tag}
          </Tag>
        </g>
      ))}
    </>
  );
}

function triangleFromSides(a: number, b: number, c: number) {
  const along = (b * b + c * c - a * a) / (2 * c);
  return { along, rise: Math.sqrt(Math.max(b * b - along * along, 0)) };
}

function TriPrismNet({ value }: DrawProps) {
  const [a, b, c, length] = floor([
    value("sideA"),
    value("sideB"),
    value("sideC"),
    value("prismLength"),
  ]);
  const { along, rise } = triangleFromSides(a, b, c);
  const { k, x0, y0 } = layoutFor(a + b + c, 2 * rise + length);
  const X = (u: number) => x0 + u * k;
  const Y = (u: number) => y0 + u * k;
  // Rectangles in a row: b, c, a — the c rectangle carries a triangle above and below.
  const startC = b;
  const top = [
    { x: X(startC), y: Y(rise) },
    { x: X(startC + c), y: Y(rise) },
    { x: X(startC + along), y: Y(0) },
  ];
  const bottomY = rise + length;
  const bottom = [
    { x: X(startC), y: Y(bottomY) },
    { x: X(startC + c), y: Y(bottomY) },
    { x: X(startC + along), y: Y(bottomY + rise) },
  ];
  const rects = [
    { x: 0, w: b, tag: "b × L" },
    { x: startC, w: c, tag: "c × L" },
    { x: startC + c, w: a, tag: "a × L" },
  ];
  return (
    <>
      {rects.map((r) => (
        <g key={r.tag}>
          <Piece d={rectPath(X(r.x), Y(rise), r.w * k, length * k)} />
          <Tag x={X(r.x + r.w / 2)} y={Y(rise + length / 2)}>
            {r.tag}
          </Tag>
        </g>
      ))}
      <Piece d={polygonPath(top)} shade={1.6} />
      <Piece d={polygonPath(bottom)} shade={1.6} />
      <Tag
        x={(top[0].x + top[1].x + top[2].x) / 3}
        y={(top[0].y + top[1].y + top[2].y) / 3 + 4}
      >
        B
      </Tag>
      <Tag
        x={(bottom[0].x + bottom[1].x + bottom[2].x) / 3}
        y={(bottom[0].y + bottom[1].y + bottom[2].y) / 3 - 4}
      >
        B
      </Tag>
    </>
  );
}

interface TubeProps {
  /** Radius of the circle above the rectangle, and of the one below. */
  top: number;
  bottom: number;
  /** The unrolled side: a rectangle of this width and height. */
  width: number;
  height: number;
  tags: { side: string; top: string; bottom: string };
}

/** A rectangle for the side with a circle tangent above and below: cylinders, near-cylinders. */
function TubeNet({ top, bottom, width, height, tags }: TubeProps) {
  const widest = Math.max(top, bottom);
  const { k, x0, y0 } = layoutFor(width, 2 * top + height + 2 * bottom);
  const X = (u: number) => x0 + u * k;
  const Y = (u: number) => y0 + u * k;
  const cx = X(widest);
  const topY = Y(top);
  const bottomY = Y(2 * top + height + bottom);
  return (
    <>
      <Piece d={rectPath(X(0), Y(2 * top), width * k, height * k)} />
      <Tag x={X(width / 2)} y={Y(2 * top + height / 2)}>
        {tags.side}
      </Tag>
      <circle
        cx={cx}
        cy={topY}
        fill={FILL}
        fillOpacity={0.19}
        r={top * k}
        stroke={SHAPE.stroke}
        strokeWidth={1.8}
      />
      <circle
        cx={cx}
        cy={bottomY}
        fill={FILL}
        fillOpacity={0.19}
        r={bottom * k}
        stroke={SHAPE.stroke}
        strokeWidth={1.8}
      />
      <Tag x={cx} y={topY}>
        {tags.top}
      </Tag>
      <Tag x={cx} y={bottomY}>
        {tags.bottom}
      </Tag>
    </>
  );
}

function CylinderNet({ value }: DrawProps) {
  const [r, h] = floor([value("radius"), value("height")]);
  return (
    <TubeNet
      bottom={r}
      height={h}
      tags={{ side: "2π × r × h", top: "π × r²", bottom: "π × r²" }}
      top={r}
      width={TWO_PI * r}
    />
  );
}

/** A circular sector with its apex at (cx, cy), bisected downward, with the given radii. */
function sectorBounds(outer: number, thetaDeg: number) {
  const half = (thetaDeg * Math.PI) / 360;
  const width = 2 * outer * (thetaDeg >= 180 ? 1 : Math.sin(half));
  const above = thetaDeg > 180 ? -outer * Math.cos(half) : 0;
  return { width, above, height: outer + above };
}

function annularSector(
  cx: number,
  cy: number,
  inner: number,
  outer: number,
  thetaDeg: number
): string {
  const half = (thetaDeg * Math.PI) / 360;
  const large = thetaDeg > 180 ? 1 : 0;
  const at = (radius: number, phi: number): Point => ({
    x: cx + radius * Math.sin(phi),
    y: cy + radius * Math.cos(phi),
  });
  const outerLeft = at(outer, -half);
  const outerRight = at(outer, half);
  const innerRight = at(inner, half);
  const innerLeft = at(inner, -half);
  const innerArc =
    inner > 0
      ? `L${pt(innerRight)} A${inner} ${inner} 0 ${large} 1 ${pt(innerLeft)}`
      : `L${pt({ x: cx, y: cy })}`;
  return `M${pt(outerLeft)} A${outer} ${outer} 0 ${large} 0 ${pt(outerRight)} ${innerArc} Z`;
}

function ConeNet({ value, slant }: DrawProps) {
  const r = value("radius");
  const l = slant.cone ?? Math.hypot(r, value("height"));
  const theta = (360 * r) / l;
  const bounds = sectorBounds(l, theta);
  const gap = 0.25 * l;
  const total = bounds.width + gap + 2 * r;
  const { k, x0, y0 } = layoutFor(total, Math.max(bounds.height, 2 * r));
  const cx = x0 + (bounds.width / 2) * k;
  const cy =
    y0 +
    (bounds.above + (Math.max(bounds.height, 2 * r) - bounds.height) / 2) * k;
  const circleX = x0 + (bounds.width + gap + r) * k;
  const circleY = y0 + (Math.max(bounds.height, 2 * r) / 2) * k;
  return (
    <>
      <Piece d={annularSector(cx, cy, 0, l * k, theta)} />
      <Tag x={cx} y={cy + l * k * 0.55}>
        π × r × l
      </Tag>
      <circle
        cx={circleX}
        cy={circleY}
        fill={FILL}
        fillOpacity={0.19}
        r={r * k}
        stroke={SHAPE.stroke}
        strokeWidth={1.8}
      />
      <Tag x={circleX} y={circleY}>
        π × r²
      </Tag>
    </>
  );
}

function PyramidNet({ value, slant }: DrawProps) {
  const [l, w] = floor([value("length"), value("width")]);
  const s1 = slant.onLength ?? value("height");
  const s2 = slant.onWidth ?? value("height");
  const { k, x0, y0 } = layoutFor(l + 2 * s2, w + 2 * s1);
  const X = (u: number) => x0 + u * k;
  const Y = (u: number) => y0 + u * k;
  const bx = s2;
  const by = s1;
  const top = [
    { x: X(bx), y: Y(by) },
    { x: X(bx + l), y: Y(by) },
    { x: X(bx + l / 2), y: Y(0) },
  ];
  const bottom = [
    { x: X(bx), y: Y(by + w) },
    { x: X(bx + l), y: Y(by + w) },
    { x: X(bx + l / 2), y: Y(by + w + s1) },
  ];
  const left = [
    { x: X(bx), y: Y(by) },
    { x: X(bx), y: Y(by + w) },
    { x: X(0), y: Y(by + w / 2) },
  ];
  const right = [
    { x: X(bx + l), y: Y(by) },
    { x: X(bx + l), y: Y(by + w) },
    { x: X(bx + l + s2), y: Y(by + w / 2) },
  ];
  const centre = (p: Point[]): Point => ({
    x: (p[0].x + p[1].x + p[2].x) / 3,
    y: (p[0].y + p[1].y + p[2].y) / 3,
  });
  return (
    <>
      <Piece d={rectPath(X(bx), Y(by), l * k, w * k)} />
      <Tag x={X(bx + l / 2)} y={Y(by + w / 2)}>
        l × w
      </Tag>
      {[top, bottom].map((tri, i) => (
        <g key={i === 0 ? "top" : "bottom"}>
          <Piece d={polygonPath(tri)} shade={1.6} />
          <Tag x={centre(tri).x} y={centre(tri).y}>
            ½ l s₁
          </Tag>
        </g>
      ))}
      {[left, right].map((tri, i) => (
        <g key={i === 0 ? "left" : "right"}>
          <Piece d={polygonPath(tri)} shade={1.6} />
          <Tag x={centre(tri).x} y={centre(tri).y}>
            ½ w s₂
          </Tag>
        </g>
      ))}
    </>
  );
}

/** Beyond this ratio of full-cone slant to frustum slant the net is a near-straight strip. */
const MAX_CONE_RATIO = 12;

function FrustumNet({ value, slant }: DrawProps) {
  const big = Math.max(value("bottomRadius"), value("topRadius"));
  const small = Math.min(value("bottomRadius"), value("topRadius"));
  const l = slant.cone ?? Math.hypot(big - small, value("height"));
  const outer =
    big > small ? (l * big) / (big - small) : Number.POSITIVE_INFINITY;
  if (outer > MAX_CONE_RATIO * l) {
    // Nearly a cylinder: draw the side as the equal-area rectangle π(R + r) wide.
    return (
      <TubeNet
        bottom={big}
        height={l}
        tags={{ side: "π × (R + r) × l", top: "π × r²", bottom: "π × R²" }}
        top={small}
        width={Math.PI * (big + small)}
      />
    );
  }
  const inner = outer - l;
  const theta = (360 * big) / outer;
  const bounds = sectorBounds(outer, theta);
  const gap = 0.2 * outer;
  const circlesHeight = 2 * big + 2 * small + 0.1 * outer;
  const height = Math.max(bounds.height, circlesHeight);
  const { k, x0, y0 } = layoutFor(bounds.width + gap + 2 * big, height);
  const cx = x0 + (bounds.width / 2) * k;
  const cy = y0 + (bounds.above + (height - bounds.height) / 2) * k;
  const circleX = x0 + (bounds.width + gap + big) * k;
  const bigY = y0 + (height / 2 - small - 0.05 * outer) * k;
  const smallY = y0 + (height / 2 + big + 0.05 * outer) * k;
  return (
    <>
      <Piece d={annularSector(cx, cy, inner * k, outer * k, theta)} />
      <Tag x={cx} y={cy + (inner + l / 2) * k}>
        π × (R + r) × l
      </Tag>
      <circle
        cx={circleX}
        cy={bigY}
        fill={FILL}
        fillOpacity={0.19}
        r={big * k}
        stroke={SHAPE.stroke}
        strokeWidth={1.8}
      />
      <Tag x={circleX} y={bigY}>
        π × R²
      </Tag>
      <circle
        cx={circleX}
        cy={smallY}
        fill={FILL}
        fillOpacity={0.19}
        r={small * k}
        stroke={SHAPE.stroke}
        strokeWidth={1.8}
      />
      <Tag x={circleX} y={smallY}>
        π × r²
      </Tag>
    </>
  );
}

const NETS: Record<
  Exclude<NetKind, "none">,
  (props: DrawProps) => ReactNode
> = {
  box: BoxNet,
  triPrism: TriPrismNet,
  cylinder: CylinderNet,
  cone: ConeNet,
  pyramid: PyramidNet,
  frustum: FrustumNet,
};

export function NetDiagram(props: NetDiagramProps) {
  const Draw = NETS[props.net];
  return (
    <DiagramFrame svgRef={props.svgRef} title={props.title}>
      <Draw slant={props.slant} value={(id) => props.values[id] ?? 0} />
      <Label color={SHAPE.muted} size={12} x={180} y={226}>
        {props.legend.join("   ·   ")}
      </Label>
    </DiagramFrame>
  );
}
